import React, { useState, useEffect, useCallback } from "react";
import { Form, Button, Card, Container, Row, Col, Alert } from "react-bootstrap";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config";

const LoginPage = () => {
  const { setAuthToken, user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Memoized role navigation handler
  const handleRoleNavigation = useCallback((role) => {
    const from = location.state?.from?.pathname;
    
    // Check if the 'from' path matches the user's role
    const isValidReturn = from && (
      (role === 'Admin' && from.startsWith('/admin')) ||
      (role === 'Doctor' && from.startsWith('/doctor')) ||
      (role === 'Patient' && from.startsWith('/patient')) ||
      (role === 'Radiologist' && from.startsWith('/radiologist'))
    );
  
    if (from && isValidReturn) {
      navigate(from);
    } else {
      // Default role-based navigation
      switch (role) {
        case "Admin":
          navigate("/admin");
          break;
        case "Doctor":
          navigate("/doctor");
          break;
        case "Patient":
          navigate("/patient");
          break;
        case "Radiologist":
          navigate("/radiologist");
          break;
        default:
          navigate("/unauthorized");
      }
    }
  }, [navigate, location.state]);

  // Handle redirect after login
  useEffect(() => {
    if (user?.role) {
      handleRoleNavigation(user.role);
    }
  }, [user, handleRoleNavigation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/user/login`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
      
      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token);
        setAuthToken(response.data.token);
        // Navigation will happen in useEffect when user is set
      } else {
        throw new Error("Token not received");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.response?.data?.message ||
        "Invalid credentials or server error. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      fluid
      className="d-flex align-items-center justify-content-center"
      style={{ 
        minHeight: "100vh",
        backgroundColor: "#f8f9fa"
      }}
    >
      <Row className="justify-content-center w-100">
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <h2 className="text-center mb-4">Login</h2>
              
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleLogin}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter username"
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter password"
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  disabled={isLoading}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </Button>
              </Form>

              <div className="text-center mt-3">
                <a href="/forgot-password" className="text-decoration-none">
                  Forgot Password?
                </a>
              </div>
              
              <div className="text-center mt-3">
                Don't have an account?{" "}
                <a href="/register" className="text-decoration-none">
                  Register here
                </a>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;
